import playIcon from "../../../../assets/icons/play-icon.webp";
import { useRef, useState, useEffect, useMemo } from "react";

const ProductQuickViewGallery = ({
  selectedMedia,
  setSelectedMedia,
  selectedColor,
  selectedSize,
  productDetails,
  handleMouseMove,
  handleMouseLeave,
  loading,
  Skeleton,
  imageRef,
  localLoading,
}) => {
  const thumbRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  /* ================= SIZE + COLOR WISE GALLERY ================= */

  const galleryImages = useMemo(() => {
    // Product media from selected color
    if (
      selectedColor?.product_media &&
      selectedColor.product_media.length > 0
    ) {
      return [...selectedColor.product_media]
        .filter((item) => item.media_type === "image")
        .sort((a, b) => a.position - b.position)
        .map((item) => item.media);
    }

    // Fallback product images
    if (
      productDetails?.product_images &&
      productDetails.product_images.length > 0
    ) {
      return [...productDetails.product_images]
        .sort((a, b) => a.position - b.position)
        .map((img) => img.media);
    }

    // Last fallback
    if (selectedColor?.color_media) {
      return [selectedColor.color_media];
    }

    return [];
  }, [selectedColor, selectedSize, productDetails]);

  /* ================= VIDEO ================= */

  const videoSource =
    selectedColor?.video_source ||
    selectedSize?.video_source ||
    productDetails?.video_source ||
    "";

  /* ================= AUTO SELECT FIRST ================= */

  useEffect(() => {
    if (galleryImages.length > 0) {
      setSelectedMedia(galleryImages[0]);
    } else if (videoSource) {
      setSelectedMedia("video");
    }
  }, [galleryImages, videoSource, setSelectedMedia]);

  /* ================= SCROLL ================= */

  const checkScroll = () => {
    if (!thumbRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = thumbRef.current;

    setCanScrollUp(scrollTop > 5);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - 5);
  };

  useEffect(() => {
    checkScroll();
  }, [galleryImages, localLoading]);

  const scrollThumbs = (direction) => {
    if (!thumbRef.current) return;

    thumbRef.current.scrollBy({
      top: direction === "up" ? -90 : 90,
      behavior: "smooth",
    });
  };

  return (
    <div
      className="product-gallery"
      style={{
        display: "flex",
        gap: "10px",
        padding: "0 20px 20px",
      }}
    >
      {/* ================= THUMBNAILS ================= */}

      <div
        style={{
          width: "80px",
          position: "relative",
          textAlign: "center",
        }}
      >
        {!localLoading && (
          <button
            onClick={() => scrollThumbs("up")}
            disabled={!canScrollUp}
            className="thumb-arrow up"
          >
            <i className="bi bi-chevron-up"></i>
          </button>
        )}

        <div
          ref={thumbRef}
          onScroll={checkScroll}
          className="thumbnail-column"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxHeight: "551px",
            overflowY: "auto",
          }}
        >
          {localLoading ? (
            Array(5)
              .fill(0)
              .map((_, i) => (
                <Skeleton
                  key={i}
                  height={70}
                  width={70}
                />
              ))
          ) : (
            <>
              {/* ================= IMAGES ================= */}

              {galleryImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`thumb-${i}`}
                  onClick={() => setSelectedMedia(img)}
                  className={`thumbnail ${
                    selectedMedia === img
                      ? "selected-thumb"
                      : ""
                  }`}
                  style={{
                    width: "70px",
                    height: "70px",
                    objectFit: "cover",
                    cursor: "pointer",
                    border:
                      selectedMedia === img
                        ? "2px solid #000"
                        : "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  loading="lazy"
                />
              ))}

              {/* ================= VIDEO ================= */}

              {videoSource && (
                <div
                  onClick={() => setSelectedMedia("video")}
                  className={`thumbnail ${
                    selectedMedia === "video"
                      ? "selected-thumb"
                      : ""
                  }`}
                  style={{
                    width: "70px",
                    height: "70px",
                    position: "relative",
                    cursor: "pointer",
                    border:
                      selectedMedia === "video"
                        ? "2px solid #000"
                        : "1px solid #ddd",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={playIcon}
                    alt="video"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {!localLoading && (
          <button
            onClick={() => scrollThumbs("down")}
            disabled={!canScrollDown}
            className="thumb-arrow down"
          >
            <i className="bi bi-chevron-down"></i>
          </button>
        )}
      </div>

      {/* ================= MAIN VIEW ================= */}

      <div
        className="main-image-container"
        style={{
          flex: 1,
          height: "546px",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseMove={
          selectedMedia !== "video"
            ? handleMouseMove
            : undefined
        }
        onMouseLeave={handleMouseLeave}
      >
        {loading ? (
          <Skeleton width="100%" height={500} />
        ) : selectedMedia === "video" ? (
          videoSource.includes("youtube") ? (
            <iframe
              src={`${videoSource}?autoplay=1&mute=1`}
              width="100%"
              height="100%"
              allow="autoplay"
              title="video"
            />
          ) : (
            <video
              controls
              autoPlay
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            >
              <source
                src={videoSource}
                type="video/mp4"
              />
            </video>
          )
        ) : selectedMedia ? (
          <img
            key={selectedMedia}
            src={selectedMedia}
            ref={imageRef}
            alt="product"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              border: "1px solid #eee",
              cursor: "crosshair",
            }}
          />
        ) : (
          <Skeleton width="100%" height={500} />
        )}
      </div>
    </div>
  );
};
export default ProductQuickViewGallery;